import { PROJECT_SHARE_TYPE } from '@components/App/Modal/Share/components/Access/components/ShareOptions/types'
import { isEmptyValue } from '@octopus-do/octopus-editor'
import {
  type Block,
  type BlocksState,
  type ColorItem,
  type ExtractableBlockData,
  Factory,
  type ID,
  isLibraryRef,
  type MonolithicBlock,
  type ProjectTheme,
  resolveSymbol,
  type SectionItem,
  type StickyNote,
  type SymbolItem,
  selectActualIds,
  selectBlocksByNode,
  selectCompletePath,
  selectEstimatesByNode,
  selectNodeChildren,
  selectNodeDescentChildren,
  selectTabArrowIds,
  selectTabNodes,
  selectTabs,
  type TabItem,
  type TagItem,
} from '@octopus-do/octopus-project-core'
import type { Notes } from '@octopus-do/octopus-project-core/dist/src/types/nodes'
import { PERSONAL_WORKSPACE_ID } from '@store/reducers/dashboard/constants'
import type { AppState } from '@store/types'

import {
  BASE_COLOR_CLOUDY,
  COLOR_BLUE,
  colorSets,
  DEFAULT_PROJECT_NAME,
} from '@/constants/constants'
import { isAdvancedPlan } from '@/constants/userSubscriptionPlans'
import { PERMISSION_TYPE } from '@/permissions'
import { PROJECT_SETTINGS_PERMISSION } from '@/permissions/project/definitions'
import type {
  Avatar,
  NodesWithBlocks,
  ProjectOrigin,
  Recipient,
  Team,
} from '@/types'
import {
  getBaseTreeType,
  isEditorValue,
  sumEstimateHoursByLineItemId,
} from '@/utils'

const EMPTY_SYMBOL_DATA: ExtractableBlockData = Factory.block.extractableData({
  title: '',
  content: '',
  colorId: '',
})
const EMPTY_IDS: ID[] = []

export const migrationProjectSelector = {
  getActualIds:
    (ids: ID[]) =>
    (state: AppState): ID[] =>
      selectActualIds(ids)(state),
  getIsCurrentUserOwnerOfProject: (state: AppState): boolean =>
    state.manager.meta.ownerId === state.auth.id,
  getIsUserOwnerOfProject:
    (id: string | null) =>
    (state: AppState): boolean =>
      id !== null && state.manager.meta.ownerId === id,

  getAnonymousComments: (state: AppState): boolean =>
    state.manager.meta.anonymous_comments,
  getCompletePath: (id: ID) => (state: AppState) =>
    selectCompletePath(id)(state),
  getProject: (state: AppState) => state.manager.project,
  getProjectId: (state: AppState): ID | null => state.manager.meta.id,
  getProjectShareType: (state: AppState) => state.manager.meta.shareType,
  getProjectLinkPermission: (state: AppState) =>
    state.manager.meta.linkPermission,
  getProjectHasPassword: (state: AppState): boolean =>
    state.manager.meta.password,
  projectIsOnlyForMembers: (state: AppState): boolean =>
    state.manager.meta.shareType === PROJECT_SHARE_TYPE.ONLY_MEMBERS,
  getProjectMembers: (state: AppState) => state.manager.meta.members,
  // A report has no canvas, and the share dialog it opens is the editor's own
  // with the parts a report has no use for taken out.
  getProjectIsReport: (state: AppState): boolean =>
    state.manager.meta.space.type === 'report',
  getProjectLegacyAccessTypeIsPrivate: (state: AppState): boolean =>
    !state.manager.meta.access.canView,
  getProjectLegacyAccessTypeIsReadOnly: (state: AppState): boolean =>
    !state.manager.meta.access.canEdit || state.artboard.isEmbed,
  getProjectLegacyAddingSectionEnabled: ({ manager, artboard }: AppState) =>
    manager.meta.access.canEdit &&
    !artboard.isEmbed &&
    artboard.mode !== 'gallery',
  getProjectImagesMode: (state: AppState) =>
    state.manager.project.settings.imageMode,
  getProjectIsTrial: (state: AppState): boolean =>
    state.manager.meta.ownerId === null && state.manager.meta.id != null,
  getIsProjectMobile: (state: AppState): boolean =>
    state.manager.project.settings.frame === 'mobile',
  getIsProjectNeutral: (state: AppState): boolean =>
    state.manager.project.settings.frame === 'neutral',
  getProjectHasChangesAndNotSaved: (state: AppState): boolean =>
    state.manager.meta.updatedAt !== null && state.manager.meta.id === null,
  getProjectIsTemplate: (state: AppState): boolean =>
    state.manager.meta.template,
  getProjectOrigin: (state: AppState): ProjectOrigin =>
    state.manager.meta.origin,
  getProjectTitle: (state: AppState): string =>
    state.manager.project.settings.title || DEFAULT_PROJECT_NAME,
  getProjectTreeType: (state: AppState) =>
    getBaseTreeType(state.manager.project.settings.tree),
  getProjectFrame: (state: AppState): string =>
    state.manager.project.settings.frame,
  getProjectCreatedAt: (state: AppState) => state.manager.meta.createdAt,
  getProjectUpdatedAt: (state: AppState) => state.manager.meta.updatedAt,
  getProjectOwnerId: (state: AppState): string | null =>
    state.manager.meta.ownerId,
  getProjectOwnerPlan: (state: AppState): string | null =>
    state.manager.meta.ownerPlan,
  getProjectTeam: (state: AppState): Team | null => state.manager.meta.team,
  getProjectIsFigmaEnabled: (state: AppState): boolean =>
    state.manager.meta.integrations?.figma === true,
  // The workspace that owns the project: its team, or the owner in personal.
  // A uuid either way — the socket room and the library items use the same key.
  getProjectWorkspaceId: (state: AppState): string | null =>
    state.manager.meta.team?.id ?? state.manager.meta.ownerId,
  // What the library endpoints take: `my` stands for the personal workspace.
  getLibraryWorkspaceParam: (state: AppState): string =>
    state.manager.meta.team?.id ?? PERSONAL_WORKSPACE_ID,
  getProjectTeamId: (state: AppState): ID | null => {
    const { team } = state.manager.meta

    return team ? team.id : null
  },
  getProjectTheme: (state: AppState): ProjectTheme =>
    state.manager.project.settings.theme,
  getProjectAccessCanEdit: (state: AppState): boolean =>
    state.manager.meta.access.canEdit,
  getProjectAccessCanComment: (state: AppState): boolean =>
    state.manager.meta.access.canComment,
  getProjectAccessCanEditContent: (state: AppState): boolean =>
    state.manager.meta.access.canEditContent,
  getProjectAvatar: (state: AppState): string | null =>
    state.manager.meta.avatar,
  getProjectAvatars: (state: AppState): Avatar[] => state.manager.meta.avatars,
  getProjectCustomAvatar: (state: AppState): string | null =>
    state.manager.meta.customAvatar,
  getProjectRecipients: (state: AppState) => {
    const {
      meta: { recipients, members },
      users,
    } = state.manager

    return [...recipients, ...members, ...users]
      .reduce((acc, user) => {
        if (!acc.some((u: any) => u.id === user.id)) {
          acc.push(user)
        }
        return acc
      }, [] as any[])
      .filter((user: any) => user !== undefined && !user.pending)
  },
  getProjectRecipientById:
    (id: string) =>
    (state: AppState): Recipient | null =>
      migrationProjectSelector
        .getProjectRecipients(state)
        .find((u) => u.id === id) || null,
  getProjectCommentableTitle:
    (id: string, type: number) =>
    (state: AppState): string => {
      if (type === 1) {
        return migrationProjectSelector.getProjectNodeTitle(id)(state)
      }

      if (type === 2) {
        return migrationProjectSelector.getProjectBlockTitle(id)(state)
      }

      return ''
    },
  getProjectHasAccessBlocker: (state: AppState): boolean =>
    state.manager.meta.access.accessBlockerType !== null &&
    ['archived', 'expired', 'private', 'password'].includes(
      state.manager.meta.access.accessBlockerType
    ),
  getProjectVariantType: (state: AppState) =>
    getBaseTreeType(state.manager.project.settings.tree),
  getProjectLegendPosition: (state: AppState) =>
    state.manager.project.settings.legendPosition,
  getProjectIsLegendEnabled: (state: AppState) =>
    state.manager.project.settings.legendPosition !== 'none',
  getProjectLegacyIsTopLegend: (state: AppState) =>
    state.manager.project.settings.legendPosition === 'top',
  getProjectLegacyAdvanced: (state: AppState): boolean =>
    state.manager.meta.ownerPlan !== null &&
    isAdvancedPlan(state.manager.meta.ownerPlan),
  getProjectDefaultColorId: ({
    manager: {
      project: {
        settings: { defaultColor },
        colors,
      },
    },
  }: AppState): ID => defaultColor || colors[0]?.id,
  getProjectColorById:
    (id: ID) =>
    (state: AppState): ColorItem => {
      const { colors, settings } = state.manager.project
      const defaultId = migrationProjectSelector.getProjectDefaultColorId(state)

      const base = colors.find((c) => c.id === id) ??
        colors.find((c) => c.id === defaultId) ?? {
          id: '',
          title: '',
          hexCode: '',
          showInLegend: false,
        }

      const { theme } = settings
      const themedHex = colorSets[base.hexCode]?.[theme] ?? base.hexCode

      return { ...base, hexCode: themedHex }
    },
  getProjectColorByHex:
    (hex: string) =>
    (state: AppState): ColorItem | undefined =>
      state.manager.project.colors.find((color) => color.hexCode === hex),
  getProjectColorHexOrDefault: (id: ID) => (state: AppState) => {
    const colorsToSearch = [...state.manager.project.colors, BASE_COLOR_CLOUDY]
    const color = colorsToSearch.find((c) => c.id === id)

    return color ? color.hexCode : COLOR_BLUE
  },
  getProjectColorForUI: (id: ID) => (state: AppState) => {
    const colorHex =
      migrationProjectSelector.getProjectColorHexOrDefault(id)(state)

    return migrationProjectSelector.getHexForUI(colorHex)(state)
  },
  /** A color kept by value, like a library one, the way the theme of the project shows it. */
  getHexForUI: (colorHex: string) => (state: AppState) => {
    const { theme } = state.manager.project.settings
    const hex = colorHex.toUpperCase()

    return colorSets[hex]?.[theme] || colorHex
  },

  // symbols
  getProjectSymbols: (state: AppState): SymbolItem[] =>
    state.manager.project.symbols,
  getProjectSymbolById:
    (id: ID) =>
    (state: AppState): SymbolItem | null =>
      state.manager.project.symbols.find((symbol) => symbol.id === id) ?? null,
  // The data a symbol renders with: its own, or — for a workspace library
  // ref — the data of the library item. A missing symbol or item gives the
  // empty stub, the way a missing symbol always did.
  getProjectSymbolData:
    (id: ID) =>
    (state: AppState): ExtractableBlockData =>
      resolveSymbol(
        migrationProjectSelector.getProjectSymbolById(id)(state) ?? undefined,
        state.library.symbol,
        state.manager.project.colors
      ) ?? EMPTY_SYMBOL_DATA,
  /** A ref whose library item is gone: the symbol is about to be purged. */
  getProjectSymbolMissing:
    (id: ID) =>
    (state: AppState): boolean => {
      const symbol = migrationProjectSelector.getProjectSymbolById(id)(state)

      return (
        symbol !== null &&
        isLibraryRef(symbol) &&
        !state.library.symbol[symbol.libraryId]
      )
    },
  getProjectBlockSymbolMissing:
    (id: ID) =>
    (state: AppState): boolean => {
      const block = state.manager.project.blocks[id]

      return block?.type === 'symbol'
        ? migrationProjectSelector.getProjectSymbolMissing(block.symbolId)(
            state
          )
        : false
    },
  getProjectSymbolLibraryId:
    (id: ID) =>
    (state: AppState): ID | null => {
      const symbol = migrationProjectSelector.getProjectSymbolById(id)(state)

      return symbol && isLibraryRef(symbol) ? symbol.libraryId : null
    },
  getProjectSymbolDependents:
    (id: ID) =>
    (state: AppState): ID[] =>
      migrationProjectSelector.getProjectSymbolById(id)(state)?.dependents ??
      EMPTY_IDS,
  // The caller may edit this symbol in the library: it is a ref to an item of
  // the workspace of the project, the item is still listed, and the caller has
  // the right. Anything else makes an edit detach the ref first.
  getCanEditSymbolInLibrary:
    (id: ID) =>
    (state: AppState): boolean => {
      const symbol = migrationProjectSelector.getProjectSymbolById(id)(state)

      if (!symbol || !isLibraryRef(symbol)) {
        return false
      }

      const item = state.library.symbol[symbol.libraryId]
      const workspaceId = migrationProjectSelector.getProjectWorkspaceId(state)
      const canEdit =
        state.user.permissions?.[
          PROJECT_SETTINGS_PERMISSION.WORKSPACE_LIBRARY_EDIT
        ]?.type === PERMISSION_TYPE.YES && state.manager.meta.access.canEdit

      return Boolean(item && item.workspaceId === workspaceId && canEdit)
    },
  getProjectSymbolWireframes: (id: ID) => (state: AppState) => {
    const { visual } = migrationProjectSelector.getProjectSymbolData(id)(state)

    return visual.type === 'wireframes' ? visual.wireframes : []
  },
  getProjectBlockOrSymbolDataByIds: (ids: ID[]) => (state: AppState) =>
    ids.reduce((acc: any[], id: ID) => {
      const block =
        migrationProjectSelector.getProjectBlockOrSymbolData(id)(state)

      return [...acc, block]
    }, []),
  getProjectBlockUnsafe: (id: ID) => (state: AppState) =>
    state.manager.project.blocks[id],
  getProjectBlockOrSymbolData: (id: ID) => (state: AppState) => {
    const block = state.manager.project.blocks[id]

    const falseBlock = Factory.block.monolithic({
      id: '',
      type: 'monolithic',
      completed: false,
      title: '',
      colorId: '',
      content: '',
    })

    if (!block) {
      return falseBlock
    }

    if (block.type === 'symbol') {
      const symbol = migrationProjectSelector.getProjectSymbolById(
        block.symbolId
      )(state)

      if (!symbol) {
        return falseBlock
      }

      const data = migrationProjectSelector.getProjectSymbolData(
        block.symbolId
      )(state)

      // @todo review this old logic
      // const ifBlockBodyEmptyWeUseBodyFromSymbol = isValueEmpty(block.content)
      //
      // const content = !ifBlockBodyEmptyWeUseBodyFromSymbol
      //   ? block.content
      //   : symbol.content

      return {
        id: block.id,
        type: 'symbol',
        completed: block.completed,

        title: data.title,
        colorId: data.colorId,
        visual: data.visual,
        content: data.content,
        symbolId: block.symbolId,
      }
    }

    if (block.type === 'monolithic') {
      return {
        ...block,
        symbolId: null,
      }
    }

    return falseBlock
  },

  // sections
  getProjectSections: (state: AppState): SectionItem[] =>
    state.manager.project.sections,
  getProjectSectionIdsSortedByIds:
    (ids: ID[]) =>
    (state: AppState): string[] =>
      state.manager.project.sections
        .filter((section) => ids.includes(section.id))
        .sort((a, b) => a.order - b.order)
        .map((section) => section.id) ?? [],
  getProjectSectionsByTabId:
    (tabId: string) =>
    ({
      manager: {
        project: { sections },
      },
    }: AppState): SectionItem[] => {
      return sections
        .filter((section) => section.tabId === tabId)
        .sort((a, b) => a.order - b.order)
    },
  getProjectSectionIsCollapsed:
    (id: ID) =>
    (state: AppState): boolean =>
      state.manager.project.sections.find((section) => section.id === id)
        ?.collapsed ?? false,
  getProjectSectionById:
    (id: ID) =>
    (state: AppState): SectionItem | null =>
      state.manager.project.sections.find((section) => section.id === id) ??
      null,

  // stickyNotes
  getProjectStickyNotes: (state: AppState) => state.manager.project.stickyNotes,
  getProjectStickyNoteById: (id: ID) => (state: AppState) =>
    state.manager.project.stickyNotes.find((note) => note.id === id) ??
    ({
      id: '',
      color: '',
      content: '',
      offset: { x: 0, y: 0 },
      size: { height: 0, width: 0 },
    } as StickyNote),

  // estimates
  getProjectEstimateSettingPrivate: (state: AppState) =>
    state.manager.project.estimates.settings.hidden,
  getProjectEstimateVat: (state: AppState) =>
    state.manager.project.estimates.settings.taxValue,
  getProjectEstimateTaxEnabled: (state: AppState) =>
    state.manager.project.estimates.settings.taxEnabled,
  getProjectEstimateCurrency: (state: AppState) =>
    state.manager.project.estimates.settings.currency,
  getProjectEstimateUnits: (state: AppState) =>
    state.manager.project.estimates.settings.units,

  // blocks
  getProjectBlocks: (state: AppState): BlocksState =>
    state.manager.project.blocks,
  getProjectBlocksOrSymbolTitlesByIds: (ids: ID[]) => (state: AppState) =>
    ids.reduce((acc: string[], id: ID) => {
      let title = null

      const block = state.manager.project.blocks[id]

      if (block) {
        if (block.type === 'symbol') {
          title = migrationProjectSelector.getProjectSymbolData(block.symbolId)(
            state
          ).title
        }

        if (block.type === 'monolithic') {
          title = block.title
        }

        if (title) {
          return [...acc, title]
        }

        return acc
      }

      return acc
    }, []),
  getProjectBlockById:
    (id: ID) =>
    (state: AppState): Block =>
      state.manager.project.blocks[id] ?? null,
  getProjectBlockTitle:
    (id: ID) =>
    (state: AppState): string => {
      const block = state.manager.project.blocks[id] as MonolithicBlock
      return block?.title ?? ''
    },
  getProjectBlockColorHex: (id: ID) => (state: AppState) => {
    const block = state.manager.project.blocks[id] as MonolithicBlock

    return (
      state.manager.project.colors.find((color) => color.id === block.colorId)
        ?.hexCode ?? '#2F7FEF'
    )
  },
  getProjectBlockIsCompleted: (id: ID) => (state: AppState) => {
    return state.manager.project.blocks[id]?.completed ?? false
  },

  // nodes
  getPageCount: (state: AppState) => state.manager.project.nodes.length,
  // getPages: (state: AppState) => (store: AppState) =>
  //   Object.values(store.manager.project)
  //     .filter((node) => currentTabSectionsGroupIds.includes(node.group))
  //     .map((node) => ({
  //       index: indexer.addAndGetIndex(node.id),
  //       name: node.name,
  //       blocks: store.blocks.byNodeId[node.id]?.map((blockId) => ({
  //         name: store.blocks[blockId].name,
  //       })),
  //     })),
  getProjectNodeByIdUnsafe: (id: ID) => (state: AppState) =>
    state.manager.project.nodes.find((node) => node.id === id),
  getProjectNodeById: (id: ID) => (state: AppState) =>
    state.manager.project.nodes.find((node) => node.id === id) || {
      id: '',
      title: '',
      colorId: '',
      parentId: null,
      collapsed: false,
      variant: 'default',
      figma: null,
      image: null,
      notes: {} as Notes,
      url: null,
    },
  getProjectNodesByIds: (ids: ID[]) => (state: AppState) =>
    state.manager.project.nodes.filter((node) => ids.includes(node.id)),
  getProjectNodeTitle:
    (id: ID) =>
    (state: AppState): string =>
      state.manager.project.nodes.find((node) => node.id === id)?.title ?? '',
  getProjectNodeColor:
    (id: ID) =>
    (state: AppState): ID | null =>
      state.manager.project.nodes.find((node) => node.id === id)?.colorId ??
      null,
  getProjectNodeColorHex: (id: ID) => (state: AppState) => {
    const nodeColorId = state.manager.project.nodes.find(
      (node) => node.id === id
    )?.colorId
    // @todo refactor this
    return (
      state.manager.project.colors.find((color) => color.id === nodeColorId)
        ?.hexCode ?? '#2F7FEF'
    )
  },
  getProjectNodeHasContentSelector: (id: ID) => (state: AppState) => {
    const node = state.manager.project.nodes.find((n) => n.id === id)

    if (!node) {
      return false
    }

    const blockIds = selectBlocksByNode(id)(state)

    return blockIds.some((blockId) => {
      const block = state.manager.project.blocks[blockId]

      if (!block) {
        return false
      }

      if (block.type === 'symbol') {
        const { content } = migrationProjectSelector.getProjectSymbolData(
          block.symbolId
        )(state)

        return isEditorValue(content) && !isEmptyValue(content)
      }

      if (block.type === 'monolithic') {
        return isEditorValue(block.content) && !isEmptyValue(block.content)
      }

      return false
    })
  },
  getProjectNodeTreePreviews: (id: ID) => (state: AppState) => {
    const children = selectNodeChildren(id)(state)

    return children.filter((childId) => childId !== id)
  },
  getProjectNodeChildIdsLength: (id: ID) => (state: AppState) => {
    const allDescendants = selectNodeDescentChildren(id)(state)

    return allDescendants.filter((childId) => childId !== id).length
  },
  getProjectLegacyTree: (state: AppState): any => state.manager.project.nodes,
  getProjectLegacyTreeAsKeyValue: (state: AppState): any =>
    state.manager.project.nodes.map((node) => ({ [node.id]: node })),
  // colors
  getProjectColors: (state: AppState): ColorItem[] => {
    const { colors, settings } = state.manager.project
    const { theme } = settings

    return colors.map((c) => ({
      ...c,
      hexCode: colorSets?.[c.hexCode]?.[theme] ?? c.hexCode,
    }))
  },

  // tags
  getProjectTags: (state: AppState): TagItem[] => state.manager.project.tags,
  getProjectTagById: (id: ID) => (state: AppState) =>
    state.manager.project.tags.find((tag) => tag.id === id) ?? {
      id: '',
      title: '',
      colorId: '',
      nodes: [],
    },

  // tabs
  getProjectTabs: (state: AppState): TabItem[] =>
    selectTabs()({
      project: state.manager.project,
      relations: state.relations,
    }),
  getProjectTabTitle:
    (id: ID) =>
    (state: AppState): string => {
      const tabs = migrationProjectSelector.getProjectTabs(state)
      return tabs.find((tab) => tab.id === id)?.title ?? ''
    },

  // arrows
  getTabArrows: (id: ID) => (state: AppState) => {
    const tabArrowIds = selectTabArrowIds(id)(state)
    const idsSet = new Set(tabArrowIds)

    return state.manager.project.arrows.filter((a) => idsSet.has(a.id))
  },
  getProjectArrow: (id: ID) => (state: AppState) =>
    state.manager.project.arrows.find((arrow) => arrow.id === id),
  getProjectArrowColorHex: (id: ID) => (state: AppState) => {
    const arrow = state.manager.project.arrows.find((a) => a.id === id)
    const block = Object.values(state.manager.project.blocks).find(
      (block) => block.id === arrow?.relation[0]
    )

    let colorId = ''
    if (block?.type === 'symbol') {
      colorId =
        migrationProjectSelector.getProjectSymbolData(block.symbolId)(state)
          .colorId || '#2F7FEF'
    }

    if (block?.type === 'monolithic') {
      colorId = block.colorId
    }

    // Стрелка может начинаться и со страницы (OCT-5140) — тогда цвет её,
    // иначе управляющие точки остаются дефолтными при цветной линии.
    if (!block) {
      colorId =
        state.manager.project.nodes.find(
          (node) => node.id === arrow?.relation[0]
        )?.colorId ?? ''
    }

    // Свой цвет стрелки важнее цвета источника (OCT-5222); пустой означает
    // «как у источника», и тогда остаётся вычисленный выше.
    if (arrow?.colorId) {
      colorId = arrow.colorId
    }

    return (
      state.manager.project.colors.find((color) => color.id === colorId)
        ?.hexCode ?? '#2F7FEF'
    )
  },

  // other
  getActiveOrFirstTabId: ({
    canvas: {
      tabs: { activeTab },
    },
    manager: {
      project: { tabs },
    },
  }: AppState) => {
    const findTab = tabs.find((tab) => tab.id === activeTab)

    if (!findTab) {
      return tabs[0]?.id || ''
    }

    return activeTab || ''
  },
  getProjectTabById: (id: ID) => (state: AppState) => {
    const tabs = migrationProjectSelector.getProjectTabs(state)
    return (
      tabs.find((tab) => tab.id === id) ?? {
        id: '',
        title: '',
      }
    )
  },

  // project storage
  getProjectStorage: (state: AppState) => state.manager.storage,

  // get project node+blocks context for ai editor generator
  getProjectNodeBlocksContext: (state: AppState) => {
    const { nodes } = state.manager.project

    return nodes.map((node) => {
      const blockIds = selectBlocksByNode(node.id)(state)

      return {
        name: node.title,
        blocks: blockIds.map((blockId) => {
          const block =
            migrationProjectSelector.getProjectBlockOrSymbolData(blockId)(state)

          if (block.content !== null) {
            try {
              const jsonText = JSON.parse(block.content)

              return {
                name: block.title,
                // @ts-expect-error
                text: jsonText.blocks.reduce((acc, block) => {
                  if (block.text) {
                    return acc ? `${acc}\n${block.text}` : block.text
                  }
                  return acc
                }, ''),
              }
            } catch (e) {
              return {
                name: block.title,
                text: block.content || '',
              }
            }
          }

          return {
            name: block.title,
            text: '',
          }
        }),
      }
    })
  },

  // external links
  getProjectExternalLink: (id: ID) => (state: AppState) =>
    state.manager.project.externalLinks.find((link) => link.id === id) ?? null,

  // estimate
  getProjectEstimateLineItems: (state: AppState) =>
    state.manager.project.estimates.lineItems,
  /**
   * "This project already keeps an estimate."
   *
   * Deliberately NOT `lineItems.length > 0`: every project — brand new ones
   * included — is created with a starter line item, independently in
   * octopus-sync and here, so that predicate is true everywhere and tells us
   * nothing. An entry in `estimates.estimates` only appears once somebody has
   * actually put hours on a page.
   */
  getProjectHasEstimateData: (state: AppState): boolean =>
    Object.keys(state.manager.project.estimates.estimates).length > 0,
  /**
   * The single source of truth for whether the Estimate toolbar icon is shown.
   * `settings.estimateMode` is TRI-STATE:
   *
   *   true      - the project owner turned it on
   *   false     - the project owner turned it off, even though estimate data
   *               exists; an explicit choice, so it wins
   *   undefined - nobody has ever touched the toggle. This is the state of
   *               every project that predates the setting, so fall back to
   *               "is this project actually using estimates".
   *
   * Do not collapse this to a boolean with `?? false`: that would hide the icon
   * for every project that has been keeping an estimate all along.
   */
  getProjectEstimateIconVisible: (state: AppState): boolean => {
    const estimateMode = state.manager.project.settings.estimateMode

    if (estimateMode !== undefined) {
      return estimateMode
    }

    return migrationProjectSelector.getProjectHasEstimateData(state)
  },
  getProjectNodeEstimatesByNodeIdAndLineItemId:
    (nodeId: ID, lineItemId: ID) => (state: AppState) => {
      const estimatesByNode = selectEstimatesByNode(nodeId)(state)

      return Object.values(state.manager.project.estimates.estimates).find(
        (estimate) =>
          estimate.lineItemId === lineItemId &&
          estimatesByNode.includes(estimate.id)
      )
    },
  getProjectNodeEstimatesByLineItemId:
    (lineItemId: ID) => (state: AppState) => {
      return Object.values(state.manager.project.estimates.estimates).filter(
        (estimate) => estimate.lineItemId === lineItemId
      )
    },
  getProjectNodeEstimatesHoursByLineItemId:
    (lineItemId: ID) => (state: AppState) => {
      return sumEstimateHoursByLineItemId(
        state.manager.project.estimates.estimates,
        lineItemId
      )
    },
  getProjectNodeEstimates: (nodeId: string) => (state: AppState) => {
    const estimatesByNode = selectEstimatesByNode(nodeId)(state)

    return (
      Object.values(state.manager.project.estimates.estimates).filter(
        (estimate) => estimatesByNode.includes(estimate.id)
      ) ?? []
    )
  },
  getProjectEstimateLinkedLineItems: (state: AppState) =>
    state.manager.project.estimates.lineItems.filter((item) => item.linked),
  getProjectEstimateTotal: (state: AppState) => {
    // const lineItemsHours = state.manager.project.estimates.lineItems.reduce(
    //   (acc, item) => {
    //     if (!item.linked) {
    //       return acc + item.value
    //     }
    //
    //     return acc
    //   },
    //   0
    // )
    //
    // const nodeEstimatesHours = Object.values(
    //   state.manager.project.estimates.estimates
    // ).reduce((acc, estimate) => {
    //   return estimate.value + acc
    // }, 0)
    //
    // return lineItemsHours + nodeEstimatesHours

    const taxEnable = state.manager.project.estimates.settings.taxEnabled
    const vat = state.manager.project.estimates.settings.taxValue

    const { subTotalHours, subTotalAmount } =
      state.manager.project.estimates.lineItems.reduce(
        (acc, item) => {
          if (!item.linked) {
            return {
              subTotalHours: acc.subTotalHours + item.value,
              subTotalAmount: acc.subTotalAmount + item.value * item.rate,
            }
          }

          const lineItemEstimatesHours = Object.values(
            state.manager.project.estimates.estimates
          )
            .filter((estimate) => estimate.lineItemId === item.id)
            .reduce((acc, estimate) => acc + estimate.value, 0)

          return {
            subTotalHours: acc.subTotalHours + lineItemEstimatesHours,
            subTotalAmount:
              acc.subTotalAmount + lineItemEstimatesHours * item.rate,
          }
        },
        {
          subTotalHours: 0,
          subTotalAmount: 0,
        }
      )

    const taxAmount = taxEnable ? (subTotalAmount / 100) * vat : 0

    return {
      subtotal: {
        hours: subTotalHours,
        amount: subTotalAmount,
      },
      tax: {
        vat,
        amount: taxAmount,
      },
      total: subTotalAmount + taxAmount,
    }
  },
  getProjectEstimateLineItemsWithNodeValue: (state: AppState) => {
    const { lineItems, estimates } = state.manager.project.estimates

    return lineItems.map((item) => {
      const nodeEstimateValue = sumEstimateHoursByLineItemId(estimates, item.id)

      const value = item.linked ? nodeEstimateValue : item.value

      return {
        ...item,
        value,
      }
    })
  },
  getPublish: (state: AppState) => state.publish,
  getPublishUpdateLoading: (state: AppState) => state.ui.publishUpdateIsLoading,
  getNodesWithBlocks: (state: AppState): NodesWithBlocks[] => {
    const activeTabId = migrationProjectSelector.getActiveOrFirstTabId(state)

    const nodeItems = selectTabNodes(activeTabId)({
      project: state.manager.project,
      relations: state.relations,
    })

    return nodeItems.map((node) => {
      const blockIds = selectBlocksByNode(node.id)(state)

      const blocks = blockIds.map((id) => ({
        ...migrationProjectSelector.getProjectBlockOrSymbolData(id)(state),
      }))

      return {
        ...node,
        blocks,
      }
    })
  },
  getColorById: (id: ID) => (state: AppState) => {
    const { colors } = state.manager.project

    return colors.find((color) => color.id === id) || null
  },
}
